'use client';

import { useState, useEffect, useCallback } from 'react';
import { detectCrisisKeywords, detectHardBlock } from '@/utils';

export interface UseContentModerationResult {
  /** True while `text` matches a crisis keyword. */
  hasCrisis: boolean;
  /**
   * True while `text` matches any HARD-BLOCK category (inappropriate language,
   * hate speech, cyberbullying, or graphic content), after the false-positive
   * allowlist is applied (MWBI-883). Named `hasInappropriate` for back-compat:
   * every consumer already gates submission and renders the warning banner on
   * this single signal, so all four categories block through one flag.
   */
  hasInappropriate: boolean;
  /** Whether the crisis advisory modal is currently open. */
  showCrisisModal: boolean;
  /** Whether the inappropriate-language modal is currently open. */
  showInappropriateModal: boolean;
  /** Close the crisis modal. */
  dismissCrisisModal: () => void;
  /** Close the inappropriate-language modal. */
  dismissInappropriateModal: () => void;
}

/**
 * Runs the shared content-moderation detectors over a single text field and
 * drives the crisis + inappropriate-language modals (MWBI-866).
 *
 * Behavior is extracted verbatim from the original inline wiring in
 * CreateNewsModal: each modal auto-opens once when its keyword first appears,
 * and re-arms only after the text stops matching — so a user is not nagged on
 * every keystroke. Crisis is informational (never blocks); callers gate
 * submission on `hasInappropriate`.
 *
 * Word lists are hydrated globally by ModerationBootstrap / moderationStore
 * (fail-open) — this hook never fetches or hardcodes them. Each call owns its
 * own state, so a component may call it per field (e.g. comment vs reply vs
 * edit) without the instances interfering.
 *
 * When a single field matches both detectors, crisis takes priority:
 * `showInappropriateModal` is suppressed while `showCrisisModal` is open, so the
 * two full-screen overlays never stack. The inappropriate modal's internal
 * "should show" state is preserved, so it surfaces as soon as the crisis modal
 * is dismissed. `hasInappropriate` is unaffected, so callers keep blocking
 * submission throughout.
 */
export function useContentModeration(text: string): UseContentModerationResult {
  // Skip detection for empty/whitespace-only text. A blank composer can never
  // match a keyword, and a component may hold several idle instances (one per
  // field), so this avoids the detectors' per-word RegExp work on every
  // keystroke of an unrelated field (MWBI-874/875).
  const hasText = text.trim().length > 0;
  const hasInappropriate = hasText && detectHardBlock(text);
  const hasCrisis = hasText && detectCrisisKeywords(text);

  const [showCrisisModal, setShowCrisisModal] = useState(false);
  const [crisisModalShown, setCrisisModalShown] = useState(false);
  const [showInappropriateModal, setShowInappropriateModal] = useState(false);
  const [inappropriateModalShown, setInappropriateModalShown] = useState(false);

  useEffect(() => {
    if (hasCrisis && !crisisModalShown) {
      setShowCrisisModal(true);
      setCrisisModalShown(true);
    }
    if (!hasCrisis) {
      setCrisisModalShown(false);
    }
  }, [hasCrisis, crisisModalShown]);

  useEffect(() => {
    if (hasInappropriate && !inappropriateModalShown) {
      setShowInappropriateModal(true);
      setInappropriateModalShown(true);
    }
    if (!hasInappropriate) {
      setInappropriateModalShown(false);
    }
  }, [hasInappropriate, inappropriateModalShown]);

  const dismissCrisisModal = useCallback(() => setShowCrisisModal(false), []);
  const dismissInappropriateModal = useCallback(() => setShowInappropriateModal(false), []);

  return {
    hasCrisis,
    hasInappropriate,
    showCrisisModal,
    // Crisis takes priority: never surface the inappropriate-language modal
    // while the crisis advisory is open, so the two overlays never stack.
    showInappropriateModal: showInappropriateModal && !showCrisisModal,
    dismissCrisisModal,
    dismissInappropriateModal,
  };
}
